import { useEffect, useId, useRef, useState } from 'react';
import { useModalA11y } from '../hooks/useModalA11y';
import {
  confirmFollowupDocumentDelivery,
  exportFollowupDocument,
  loadFollowupDocumentReference,
} from '../services/followupDocumentDelivery';
import { exportPlanToWord } from '../services/exportToWord';
import {
  FOLLOWUP_DOCUMENT_CHANNELS,
  isDeliveryProofForSelection,
} from './followupDocumentDeliveryUi';

export default function FollowupDocumentDeliveryModal({
  client,
  consultationId,
  onClose,
  onConfirmed,
}) {
  const clientId = String(client?.id || '');
  const prenom = (client?.form?.prenom || client?.prenom || 'la cliente').trim();
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef(null);

  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [proof, setProof] = useState(null);
  const [channel, setChannel] = useState('');
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [postConfirmError, setPostConfirmError] = useState(null);
  const selectionKey = [
    clientId,
    consultationId,
    loaded?.reference?.contentSha256 || '',
    loaded?.reference?.revisionSha256 || '',
    loaded?.reference?.clientFormSha256 || '',
  ].join(':');
  const selectionKeyRef = useRef(selectionKey);
  useModalA11y({ onClose, panelRef, busy: exporting || confirming });

  // Le token est invalidé au changement de cliente/version et au démontage.
  // Ainsi un export Word encore en construction ne peut jamais déclencher son
  // téléchargement après fermeture de la fenêtre ou navigation vers une autre fiche.
  useEffect(() => {
    selectionKeyRef.current = selectionKey;
    return () => {
      selectionKeyRef.current = `invalid:${selectionKey}:${Date.now()}`;
    };
  }, [selectionKey]);

  useEffect(() => {
    let cancelled = false;
    setLoaded(null);
    setProof(null);
    setChannel('');
    setReviewConfirmed(false);
    setConfirmed(null);
    setPostConfirmError(null);
    setError(null);
    setLoading(true);
    (async () => {
      try {
        const exact = await loadFollowupDocumentReference({ clientId, consultationId });
        if (!cancelled) setLoaded(exact);
      } catch (loadError) {
        if (!cancelled) setError(loadError?.message || 'Impossible de charger la version exacte.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, consultationId]);

  // La case vaut seulement pour le Word exporté et le canal actuellement
  // sélectionnés. Changer l'un des deux impose une nouvelle confirmation.
  useEffect(() => {
    setReviewConfirmed(false);
  }, [proof?.artifactSha256, proof?.consultationId, channel]);

  const handleExport = async () => {
    if (!loaded || exporting || confirming) return;
    const capturedSelectionKey = selectionKey;
    const capturedReference = loaded.reference;
    const isCurrentSelection = () => selectionKeyRef.current === capturedSelectionKey;
    const shouldCommitArtifact = async () => {
      if (!isCurrentSelection()) return false;
      try {
        const latest = await loadFollowupDocumentReference({
          clientId,
          consultationId,
        });
        const reference = latest.reference;
        return isCurrentSelection()
          && reference.status === 'ready'
          && reference.clientId === capturedReference.clientId
          && reference.consultationId === capturedReference.consultationId
          && reference.contentSha256 === capturedReference.contentSha256
          && reference.revisionSha256 === capturedReference.revisionSha256
          && reference.clientFormSha256 === capturedReference.clientFormSha256;
      } catch {
        return false;
      }
    };
    setExporting(true);
    setError(null);
    setProof(null);
    setChannel('');
    try {
      const exported = await exportFollowupDocument({
        client,
        loaded,
        exportPlan: (exactClient, exactConsultation, exactPlan) => exportPlanToWord(
          exactClient,
          exactConsultation,
          exactPlan,
          { shouldCommitArtifact },
        ),
      });
      if (!isCurrentSelection()) {
        throw new Error('La cliente ou la version a changé pendant l’export. Recommence la remise.');
      }
      setProof(exported);
    } catch (exportError) {
      setError(exportError?.message || 'Le document Word n’a pas pu être exporté.');
    } finally {
      setExporting(false);
    }
  };

  const handleConfirm = async () => {
    if (!isDeliveryProofForSelection(proof, clientId, consultationId, channel)
        || reviewConfirmed !== true || confirming) {
      setError('Exporte et relis le document exact, puis choisis son canal de remise.');
      return;
    }
    setConfirming(true);
    setError(null);
    try {
      const result = await confirmFollowupDocumentDelivery({
        proof,
        documentChannel: channel,
        clinicalConfirmed: true,
      });
      setConfirmed(result);
      try {
        await onConfirmed?.(result, loaded?.consultation || null);
      } catch (callbackError) {
        setPostConfirmError(
          callbackError?.message || 'La remise est confirmée, mais le cockpit doit être actualisé.',
        );
      }
    } catch (confirmError) {
      setError(confirmError?.message || 'La remise du document n’a pas pu être confirmée.');
    } finally {
      setConfirming(false);
    }
  };

  const handleRetryRefresh = async () => {
    if (!confirmed || confirming) return;
    setConfirming(true);
    setPostConfirmError(null);
    try {
      await onConfirmed?.(confirmed, loaded?.consultation || null);
    } catch (callbackError) {
      setPostConfirmError(
        callbackError?.message || 'La remise est confirmée, mais le cockpit doit être actualisé.',
      );
    } finally {
      setConfirming(false);
    }
  };

  const referenceReady = loaded?.reference?.status === 'ready';
  const alreadyConfirmed = loaded?.reference?.status === 'confirmed';
  const completedReference = confirmed || (alreadyConfirmed ? loaded.reference : null);
  const canConfirm = isDeliveryProofForSelection(proof, clientId, consultationId, channel)
    && reviewConfirmed === true
    && !confirming;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      style={{
        position: 'fixed', inset: 0, zIndex: 9600, padding: 'min(5vh, 42px) min(4vw, 44px)',
        display: 'grid', placeItems: 'center', background: 'rgba(18, 25, 20, .62)',
        backdropFilter: 'blur(7px)',
      }}
    >
      <section
        ref={panelRef}
        style={{
          width: 'min(760px, 100%)', maxHeight: '90vh', overflow: 'auto',
          borderRadius: 18, border: '1px solid rgba(26, 46, 31, .14)',
          background: 'var(--jrn-bg, #FAF8F1)', color: 'var(--jrn-ink, #1A2E1F)',
          boxShadow: '0 28px 90px rgba(11, 24, 16, .28)',
        }}
      >
        <header style={{ padding: '26px 30px 22px', borderBottom: '1px solid var(--jrn-border, #E6E1D6)', display: 'flex', gap: 20, justifyContent: 'space-between' }}>
          <div>
            <p style={{ margin: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--jrn-text-soft, #6E695F)' }}>
              Remise documentaire · version de suivi
            </p>
            <h2 id={titleId} style={{ margin: '7px 0 0', fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(24px, 4vw, 34px)', fontWeight: 500, fontStyle: 'italic', lineHeight: 1.05 }}>
              Le programme de {prenom}, prêt à partir
            </h2>
            <p id={descriptionId} style={{ margin: '11px 0 0', maxWidth: 560, fontSize: 13, lineHeight: 1.55, color: 'var(--jrn-text-soft, #6E695F)' }}>
              Le SaaS lie la remise à la version cloud exacte et à l’empreinte du Word exporté.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={exporting || confirming} className="jrn-btn jrn-btn--ghost" aria-label="Fermer la remise documentaire">
            Fermer
          </button>
        </header>

        <div style={{ padding: '24px 30px 30px' }}>
          <ol aria-label="Étapes de la remise" style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid rgba(196, 160, 80, .34)', borderRadius: 12, overflow: 'hidden', background: '#fffdf8' }}>
            {[
              ['01', 'Version liée', Boolean(loaded)],
              ['02', 'Word exporté', Boolean(proof)],
              ['03', 'Remise confirmée', Boolean(confirmed || alreadyConfirmed)],
            ].map(([number, label, done], index) => (
              <li key={number} style={{ padding: '12px 14px', borderLeft: index ? '1px solid rgba(196, 160, 80, .24)' : 'none' }}>
                <span style={{ display: 'block', fontSize: 9.5, letterSpacing: '.12em', color: done ? '#2E5E3E' : '#9A9589' }}>{done ? '✓' : number}</span>
                <strong style={{ display: 'block', marginTop: 3, fontSize: 12.5, fontWeight: 600 }}>{label}</strong>
              </li>
            ))}
          </ol>

          {loading && <p role="status" style={{ margin: 0 }}>Vérification de la version cloud exacte…</p>}
          {error && <div role="alert" className="jrn-error" style={{ marginBottom: 18 }}>⚠ {error}</div>}

          {!loading && completedReference && (
            <div role="status" style={{ padding: 18, borderRadius: 12, background: '#EAF3EC', border: '1px solid rgba(46, 94, 62, .22)' }}>
              <strong>Remise documentaire confirmée</strong>
              <p style={{ margin: '5px 0 0', fontSize: 13 }}>
                Canal : {FOLLOWUP_DOCUMENT_CHANNELS.find((item) => item.value === completedReference.documentChannel)?.label || completedReference.documentChannel}
                {completedReference.confirmedAt
                  ? ` · ${new Date(completedReference.confirmedAt).toLocaleString('fr-CH')}`
                  : ''}.
              </p>
              {postConfirmError && (
                <div role="alert" style={{ marginTop: 12, fontSize: 12.5, color: '#7A3F16' }}>
                  Remise confirmée. Actualisation incomplète : {postConfirmError}
                  <button
                    type="button"
                    onClick={handleRetryRefresh}
                    disabled={confirming}
                    className="jrn-btn jrn-btn--ghost"
                    style={{ marginLeft: 10 }}
                  >
                    {confirming ? 'Actualisation…' : 'Réessayer l’actualisation'}
                  </button>
                </div>
              )}
              <button type="button" onClick={onClose} disabled={confirming} className="jrn-btn jrn-btn--primary" style={{ marginTop: 14 }}>
                Terminer
              </button>
            </div>
          )}

          {!loading && referenceReady && !completedReference && (
            <>
              <div style={{ padding: 18, borderRadius: 12, background: '#fffdf8', border: '1px solid var(--jrn-border, #E6E1D6)', display: 'flex', gap: 18, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div>
                  <strong style={{ fontSize: 14 }}>Version cloud verrouillée</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--jrn-text-soft, #6E695F)' }}>
                    Référence {String(consultationId).slice(0, 8)} · aucun autre brouillon ne sera remis.
                  </p>
                </div>
                <button type="button" onClick={handleExport} disabled={exporting || confirming} className="jrn-btn jrn-btn--primary">
                  {exporting ? 'Création du Word…' : proof ? 'Réexporter le Word exact' : 'Exporter le Word exact'}
                </button>
              </div>

              {proof && (
                <div style={{ marginTop: 22 }}>
                  <fieldset disabled={exporting || confirming} style={{ margin: 0, padding: 0, border: 0 }}>
                    <legend style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 20, fontWeight: 500 }}>Comment le document sera-t-il remis ?</legend>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))', gap: 10, marginTop: 12 }}>
                      {FOLLOWUP_DOCUMENT_CHANNELS.map((item) => (
                        <label key={item.value} style={{ display: 'block', padding: 14, borderRadius: 11, border: `1px solid ${channel === item.value ? '#2E5E3E' : 'var(--jrn-border, #E6E1D6)'}`, background: channel === item.value ? '#EAF3EC' : '#fffdf8', cursor: 'pointer' }}>
                          <input type="radio" name="followup-document-channel" value={item.value} checked={channel === item.value} onChange={() => setChannel(item.value)} />
                          <strong style={{ marginLeft: 8, fontSize: 13 }}>{item.label}</strong>
                          <span style={{ display: 'block', margin: '7px 0 0 23px', fontSize: 11.5, lineHeight: 1.4, color: 'var(--jrn-text-soft, #6E695F)' }}>{item.detail}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 18, padding: 15, borderRadius: 11, background: 'rgba(196, 160, 80, .10)', border: '1px solid rgba(196, 160, 80, .28)', cursor: channel ? 'pointer' : 'not-allowed', opacity: channel ? 1 : .55 }}>
                    <input type="checkbox" checked={reviewConfirmed} disabled={!channel || exporting || confirming} onChange={(event) => setReviewConfirmed(event.target.checked)} style={{ marginTop: 2 }} />
                    <span style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                      J’ai relu le document Word exact exporté et je confirme qu’il sera remis à {prenom} par le canal sélectionné.
                    </span>
                  </label>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                    <button type="button" onClick={handleConfirm} disabled={!canConfirm} className="jrn-btn jrn-btn--primary">
                      {confirming ? 'Confirmation…' : 'Confirmer la remise documentaire'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
